/**
 * PetpoojaSyncTab — daily Petpooja Excel/CSV upload + sync history.
 *
 * Sprint 2026-08 · Phase 7 (Restaurant/QSR mode).
 *
 * Owner spec:
 *   • Petpooja doesn't expose an API. Instead, we consume their
 *     "Item-wise Sales" export (2 columns: Item Name, Quantity Sold).
 *   • Native format is now .xlsx (Excel) — the current Petpooja UI
 *     defaults to Excel export. We still accept legacy .csv/.tsv
 *     silently so users mid-workflow are not blocked.
 *   • Parse in browser (React + SheetJS) — zero token cost, no server
 *     round-trip for the raw file. We POST only the parsed JSON to the
 *     backend.
 *   • Unmapped menu items don't block ingestion — they queue up for
 *     progressive onboarding via the Recipes tab.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import {
    Upload,
    FileSpreadsheet,
    RotateCcw,
    Calendar,
    CheckCircle2,
    AlertCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api, fmtDate } from '../../lib/api';


/**
 * Parse a Petpooja "Item-wise Sales" CSV in-browser.
 *
 * The Petpooja report EMB accepts is minimal: 2 columns — item name and
 * quantity sold. Some exports use tabs, others use commas; some quote
 * values, some don't. This parser tolerates all common variants and
 * silently skips blank/header rows.
 *
 * @param {string} raw — the CSV file as a UTF-8 string
 * @returns {Array<{name:string, qty:number}>}
 */
export function parsePetpoojaCsv(raw) {
    if (!raw) return [];
    // Handle BOM (Excel exports frequently include it).
    if (raw.charCodeAt(0) === 0xfeff) raw = raw.slice(1);
    const lines = raw.split(/\r?\n/);
    const rows = [];
    for (let idx = 0; idx < lines.length; idx++) {
        const line = lines[idx];
        if (!line || !line.trim()) continue;
        // Detect delimiter — comma or tab.
        const delim = line.includes('\t') && !line.includes(',') ? '\t' : ',';
        // Naive split — Petpooja doesn't use embedded commas inside item
        // names in practice, so we skip full RFC-4180 quoting logic.
        const parts = line.split(delim).map((c) => c.replace(/^"|"$/g, '').trim());
        if (parts.length < 2) continue;
        const [name, qtyRaw] = parts;
        // Skip obvious header rows.
        if (idx === 0 && /item|name|qty|quantity|sold/i.test(name)) continue;
        const qty = Number(String(qtyRaw).replace(/[^\d.\-]/g, ''));
        if (!name || !Number.isFinite(qty) || qty <= 0) continue;
        rows.push({ name, qty });
    }
    return rows;
}


/**
 * Parse a Petpooja "Item-wise Sales" Excel workbook (.xlsx / .xls).
 *
 * Real Petpooja export format (verified against a live shakewallah.xlsx):
 *
 *   Row 1-3 : metadata — Date / Name / Restaurant Name
 *   Row 4-5 : blank
 *   Row 6   : header — [Category, Item, Qty.]
 *   Row 7-10: report summary — Total / Min. / Max. / Avg.  (skip)
 *   Row 11+ : data rows
 *                • Column A = category (only on group start, then blank
 *                  until the next category → we do NOT rely on it)
 *                • Column B = item name  ← what we want
 *                • Column C = quantity   ← what we want
 *                • Category cell = "Sub Total" and Item = "" for group
 *                  subtotals → skip these rows
 *
 * Robustness:
 *   • Auto-detects the header row within the first 30 rows even if
 *     Petpooja re-orders the summary block in future.
 *   • Skips Sub Total / Grand Total / Total / Min. / Max. / Avg. rows
 *     (they always have an empty Item column, but we double-guard).
 *   • Falls back to a 2-column layout (Item, Qty) if we detect the
 *     older "Item Name, Quantity Sold" format some accounts still use.
 *
 * @param {ArrayBuffer} buffer — the raw .xlsx bytes
 * @returns {Array<{name:string, qty:number}>}
 */
export function parsePetpoojaXlsx(buffer) {
    if (!buffer) return [];
    let workbook;
    try {
        workbook = XLSX.read(buffer, { type: 'array' });
    } catch {
        return [];
    }
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];
    const sheet = workbook.Sheets[sheetName];
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: true });
    if (!aoa.length) return [];

    // Header detection — find the row that contains "Item" and "Qty" columns.
    // We identify the two column indexes (nameCol / qtyCol) since Petpooja
    // uses 3 columns [Category, Item, Qty.] but legacy exports were just
    // [Item Name, Quantity Sold].
    let startIdx = 0;
    let nameCol = 0;
    let qtyCol = 1;
    for (let i = 0; i < Math.min(aoa.length, 30); i++) {
        const row = aoa[i] || [];
        const cells = row.map((c) => String(c ?? '').toLowerCase().trim());
        const itemIdx = cells.findIndex((v) =>
            /^(item[_ ]?name|item|product|name|menu[_ ]?item)$/.test(v),
        );
        const qtyIdx = cells.findIndex((v) =>
            /^(qty\.?|quantity(\s+sold)?|units|sold)$/.test(v),
        );
        if (itemIdx >= 0 && qtyIdx >= 0 && itemIdx !== qtyIdx) {
            startIdx = i + 1;
            nameCol = itemIdx;
            qtyCol = qtyIdx;
            break;
        }
    }

    // Values in column A that indicate a summary / subtotal row.
    // These rows have an empty Item cell — we skip them defensively so a
    // future Petpooja layout tweak (e.g. subtotal in the Item column)
    // still gets filtered out.
    const skipCategory = /^(sub[- ]?total|grand[- ]?total|total|min\.?|max\.?|avg\.?|average|sum)$/i;

    const rows = [];
    for (let i = startIdx; i < aoa.length; i++) {
        const row = aoa[i] || [];
        const catCell = String(row[0] ?? '').trim();
        const rawName = row[nameCol];
        const rawQty = row[qtyCol];
        const name = String(rawName ?? '').trim();

        // Skip if category cell is a stats/subtotal marker (Petpooja puts
        // "Sub Total", "Total", "Min." etc. in the category column with an
        // empty item cell).
        if (skipCategory.test(catCell)) continue;

        // Also skip if the name itself looks like a summary row (defensive).
        if (skipCategory.test(name)) continue;
        if (!name) continue;

        const qty = typeof rawQty === 'number'
            ? rawQty
            : Number(String(rawQty ?? '').replace(/[^\d.\-]/g, ''));
        if (!Number.isFinite(qty) || qty <= 0) continue;

        rows.push({ name, qty });
    }
    return rows;
}


/**
 * Read + parse a Petpooja export file — auto-detects Excel vs CSV/TSV
 * from the file extension (falling back to CSV if extension is missing).
 *
 * @param {File} file
 * @returns {Promise<Array<{name:string, qty:number}>>}
 */
export async function readPetpoojaFile(file) {
    if (!file) return [];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm' || ext === 'xlsb') {
        const buf = await file.arrayBuffer();
        return parsePetpoojaXlsx(buf);
    }
    // CSV / TSV / plain text fallback.
    const txt = await file.text();
    return parsePetpoojaCsv(txt);
}


export function PetpoojaSyncTab() {
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [rows, setRows] = useState([]);           // parsed CSV rows
    const [filename, setFilename] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [history, setHistory] = useState(null);   // list of past syncs
    const [conflict, setConflict] = useState(null); // {date, rows} awaiting overwrite confirm
    const fileInputRef = useRef(null);

    const loadHistory = useCallback(async () => {
        try {
            const r = await api.listPetpoojaSyncs({ limit: 60 });
            setHistory(r.items || []);
        } catch (e) {
            setHistory([]);
        }
    }, []);
    useEffect(() => { loadHistory(); }, [loadHistory]);

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        setFilename(file.name);
        try {
            const parsed = await readPetpoojaFile(file);
            if (parsed.length === 0) {
                toast.error(
                    'No sales rows detected. Expected 2 columns: "Item Name" and "Quantity Sold".',
                );
                setRows([]);
                return;
            }
            setRows(parsed);
            toast.success(`Parsed ${parsed.length} sales row${parsed.length === 1 ? '' : 's'}.`);
        } catch (e) {
            toast.error('Could not read the file. Please check the format (Excel .xlsx recommended).');
            setRows([]);
        }
    }, []);

    const totalQty = rows.reduce((s, r) => s + r.qty, 0);

    const doSync = async (replaceExisting = false) => {
        if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            toast.error('Pick the sales date first (YYYY-MM-DD).');
            return;
        }
        if (rows.length === 0) {
            toast.error('Upload an Excel export before syncing.');
            return;
        }
        setSyncing(true);
        try {
            const r = await api.petpoojaSync({
                date,
                rows,
                replace_existing: replaceExisting,
            });
            const stats = r.stats || {};
            toast.success(
                `Sync complete: ${stats.mapped_rows || 0} mapped, ${stats.unmapped_rows || 0} unmapped. ` +
                `${stats.ledger_entries || 0} stock deductions written.`,
                { duration: 6000 },
            );
            // Reset input state for next sync
            setRows([]);
            setFilename('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            await loadHistory();
        } catch (e) {
            if (e?.response?.status === 409) {
                setConflict({ date, rows });
                return;
            }
            toast.error(e?.response?.data?.detail || 'Sync failed.');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="petpooja-sync-tab">
            <Card className="p-4 space-y-3">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h3 className="font-heading text-lg">Daily Petpooja sync</h3>
                        <p className="text-xs text-muted-foreground max-w-md">
                            Upload today's Petpooja <em>Item-wise Sales</em> Excel export. We
                            parse it in your browser (no token cost) and update stock immediately
                            for every menu item that has a recipe.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Label htmlFor="pp-sync-date" className="text-xs whitespace-nowrap">
                            Sales date:
                        </Label>
                        <Input
                            id="pp-sync-date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-[160px]"
                            data-testid="pp-sync-date"
                        />
                    </div>
                </div>

                <div className="rounded-lg border-2 border-dashed border-border/70 bg-muted/20 p-6 text-center">
                    <FileSpreadsheet size={28} className="mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm font-medium mb-1">
                        {filename ? filename : 'Drop your Petpooja Excel file here'}
                    </div>
                    <div className="text-[11px] text-muted-foreground mb-3">
                        Expected columns: <code>Item Name</code>, <code>Quantity Sold</code>.
                        Excel (.xlsx) preferred · CSV / TSV also accepted.
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.xlsm,.xlsb,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.csv,text/csv,.tsv,text/tab-separated-values,text/plain"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        className="hidden"
                        data-testid="pp-sync-file-input"
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="pp-sync-choose-file"
                    >
                        <Upload size={14} />
                        {filename ? 'Choose different file' : 'Choose file'}
                    </Button>
                </div>

                {rows.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                                Preview — {rows.length} items · total qty {totalQty.toFixed(2)}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs gap-1"
                                onClick={() => { setRows([]); setFilename(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                            >
                                <RotateCcw size={12} /> Clear
                            </Button>
                        </div>
                        <div className="max-h-64 overflow-y-auto rounded-md border border-border/60">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-1.5 font-medium">Menu item</th>
                                        <th className="text-right px-3 py-1.5 font-medium">Qty sold</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(0, 200).map((r, i) => (
                                        <tr key={i} className="border-t border-border/50">
                                            <td className="px-3 py-1.5">{r.name}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums font-mono">{r.qty}</td>
                                        </tr>
                                    ))}
                                    {rows.length > 200 && (
                                        <tr className="border-t border-border/50">
                                            <td colSpan={2} className="px-3 py-2 text-center text-xs text-muted-foreground">
                                                +{rows.length - 200} more rows (all will be synced)
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <Button
                                onClick={() => doSync(false)}
                                disabled={syncing}
                                data-testid="pp-sync-submit"
                            >
                                {syncing ? 'Syncing…' : `Sync ${rows.length} rows`}
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            <Card className="p-0 overflow-hidden">
                <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
                    <Calendar size={16} className="text-muted-foreground" />
                    <div className="font-medium text-sm">Sync history</div>
                </div>
                {history === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-8" />
                        ))}
                    </div>
                ) : history.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                        No Petpooja syncs yet. Upload today's CSV to get started.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/30 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Date</th>
                                    <th className="text-right px-4 py-2 font-medium">Mapped</th>
                                    <th className="text-right px-4 py-2 font-medium">Unmapped</th>
                                    <th className="text-left px-4 py-2 font-medium">Uploaded</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((s) => (
                                    <tr key={s.id} className="border-t border-border hover:bg-muted/30">
                                        <td className="px-4 py-2 font-mono">{s.date}</td>
                                        <td className="px-4 py-2 text-right">
                                            <Badge variant="secondary" className="gap-1">
                                                <CheckCircle2 size={11} /> {s.mapped_count || 0}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            {s.unmapped_count ? (
                                                <Badge variant="outline" className="gap-1 border-amber-400/60 text-amber-700 dark:text-amber-300">
                                                    <AlertCircle size={11} /> {s.unmapped_count}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-muted-foreground text-xs">
                                            {fmtDate(s.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <AlertDialog open={!!conflict} onOpenChange={(v) => { if (!v) setConflict(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Sync already exists for {conflict?.date}</AlertDialogTitle>
                        <AlertDialogDescription>
                            A Petpooja sync for this date is already recorded. Replacing it
                            will reverse the previous stock deductions and re-apply them
                            from this file. Continue?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => {
                                setConflict(null);
                                await doSync(true);
                            }}
                        >
                            Replace &amp; sync
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
